import React, { useState, useEffect } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { Box, Button, InputAdornment, List, ListItemButton, TextField, Typography, CircularProgress } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useSnackbar } from 'notistack';
import BankingService from 'services/banking.service';
import { EPixOperationType, EBeneficiaryType } from 'types/banking';
import { IBeneficiary } from 'common/beneficiary.interface';
import { formatCurrency, parseCurrency, maskCpfCnpj } from 'utils/banking';

type FlowProps = {
  onSuccess: () => void;
  onCancel: () => void;
  onSubmitting: (loading: boolean) => void;
};

const PixByFavorecido = ({ onSuccess, onCancel, onSubmitting }: FlowProps) => {
  const [selected, setSelected] = useState<IBeneficiary | null>(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [beneficiaries, setBeneficiaries] = useState<IBeneficiary[]>([]);
  const [fetchingBeneficiaries, setFetchingBeneficiaries] = useState(true);
  const bankingService = new BankingService();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    let isMounted = true;

    const fetchBeneficiaries = async () => {
      try {
        setFetchingBeneficiaries(true);
        const response = await bankingService.getBeneficiaries({ limit: 100, page: 1 });
        if (isMounted) {
          setBeneficiaries(response.items);
        }
      } catch (error) {
        enqueueSnackbar('Erro ao carregar favorecidos', { variant: 'error' });
      } finally {
        if (isMounted) {
          setFetchingBeneficiaries(false);
        }
      }
    };

    fetchBeneficiaries();
    return () => {
      isMounted = false;
    };
  }, []);

  const filteredFavorecidos = beneficiaries.filter((fav) => {
    if (!fav.isActive) return false;
    const term = search.toLowerCase();
    return (fav.businessName || '').toLowerCase().includes(term) || (fav.taxNumber && fav.taxNumber.includes(term.replace(/\D/g, '')));
  });

  if (!selected) {
    return (
      <>
        <TextField
          fullWidth
          variant="standard"
          placeholder="Buscar favorecido"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 3 }}
          InputLabelProps={{ shrink: true, sx: { fontSize: '16px', fontWeight: 500 } }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            sx: { fontSize: '14px' },
          }}
        />

        {fetchingBeneficiaries && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        )}

        {!fetchingBeneficiaries && filteredFavorecidos.length === 0 && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <Typography color="text.secondary">{search ? 'Nenhum favorecido encontrado' : 'Nenhum favorecido cadastrado'}</Typography>
          </Box>
        )}

        {!fetchingBeneficiaries && filteredFavorecidos.length > 0 && (
          <List sx={{ maxHeight: 420, overflow: 'auto' }}>
            {filteredFavorecidos.map((fav) => (
              <ListItemButton
                key={fav.id}
                onClick={() => setSelected(fav)}
                sx={{
                  border: '1px solid',
                  borderColor: 'grey.200',
                  borderRadius: 2,
                  mb: 1.5,
                  p: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  '&:hover': {
                    backgroundColor: 'transparent',
                    borderColor: 'primary.main',
                  },
                }}
              >
                <Box>
                  <Typography fontWeight={600}>{fav.businessName}</Typography>
                  {fav.taxNumber && (
                    <Typography variant="body2" color="text.secondary">
                      {fav.beneficiaryType === EBeneficiaryType.INDIVIDUAL ? 'CPF' : 'CNPJ'}: {maskCpfCnpj(fav.taxNumber)}
                    </Typography>
                  )}
                </Box>

                <Box textAlign="right">
                  {fav.pixKey ? (
                    <>
                      <Typography fontWeight={600}>PIX</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {fav.pixKeyType}: {fav.pixKey}
                      </Typography>
                    </>
                  ) : (
                    <>
                      {fav.bankCode && <Typography fontWeight={600}>Banco: {fav.bankCode}</Typography>}
                      {fav.branch && (
                        <Typography variant="body2" color="text.secondary">
                          Agência: {fav.branch}
                        </Typography>
                      )}
                      {fav.accountNumber && (
                        <Typography variant="body2" color="text.secondary">
                          Conta: {fav.accountNumber}
                          {fav.accountDigit ? `-${fav.accountDigit}` : ''}
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              </ListItemButton>
            ))}
          </List>
        )}
      </>
    );
  }

  return (
    <Formik
      initialValues={{
        amount: '',
        date: new Date().toLocaleDateString('en-CA'),
        description: '',
      }}
      validationSchema={Yup.object({
        amount: Yup.string().required('Valor obrigatório'),
        date: Yup.string().required('Data obrigatória'),
      })}
      validateOnMount
      onSubmit={async (values) => {
        try {
          setLoading(true);
          onSubmitting(true);

          await bankingService.createTransaction(EPixOperationType.BENEFICIARY, {
            beneficiaryId: selected.id,
            value: parseCurrency(values.amount),
            transferDate: values.date,
            description: values.description || undefined,
          });

          onSuccess();
          enqueueSnackbar('Transação criada com favorecido com sucesso.', { variant: 'success' });
        } catch (error) {
          enqueueSnackbar('Erro ao criar transação com favorecido. Tente novamente.', { variant: 'error' });
        } finally {
          setLoading(false);
          onSubmitting(false);
        }
      }}
    >
      {({ values, errors, touched, handleChange, setFieldValue, handleSubmit, isValid }) => (
        <form onSubmit={handleSubmit}>
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'grey.200',
              borderRadius: 2,
              mb: 1.5,
              p: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
            }}
          >
            <Box>
              <Typography fontWeight={600}>{selected.businessName}</Typography>
              {selected.taxNumber && (
                <Typography variant="body2" color="text.secondary">
                  {selected.beneficiaryType === EBeneficiaryType.INDIVIDUAL ? 'CPF' : 'CNPJ'}: {maskCpfCnpj(selected.taxNumber)}
                </Typography>
              )}
            </Box>
            <Box textAlign="right">
              {selected.pixKey ? (
                <>
                  <Typography fontWeight={600}>PIX</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selected.pixKeyType}: {selected.pixKey}
                  </Typography>
                </>
              ) : (
                <>
                  {selected.bankCode && <Typography fontWeight={600}>Banco: {selected.bankCode}</Typography>}
                  {selected.branch && (
                    <Typography variant="body2" color="text.secondary">
                      Agência: {selected.branch}
                    </Typography>
                  )}
                  {selected.accountNumber && (
                    <Typography variant="body2" color="text.secondary">
                      Conta: {selected.accountNumber}
                      {selected.accountDigit ? `-${selected.accountDigit}` : ''}
                    </Typography>
                  )}
                </>
              )}
            </Box>
          </Box>

          <TextField
            fullWidth
            variant="standard"
            label="Digite quanto quer pagar (R$) *"
            placeholder="R$ 0,00"
            value={values.amount}
            onChange={(e) => setFieldValue('amount', formatCurrency(e.target.value))}
            error={Boolean(touched.amount && errors.amount)}
            helperText={touched.amount && errors.amount}
            sx={{ mb: 3 }}
            InputLabelProps={{ shrink: true, sx: { fontSize: '16px', fontWeight: 500 } }}
            InputProps={{ sx: { fontSize: '14px' } }}
            disabled={loading}
          />

          <TextField
            fullWidth
            variant="standard"
            label="Data de transferência *"
            name="date"
            type="date"
            value={values.date}
            onChange={handleChange}
            error={Boolean(touched.date && errors.date)}
            helperText={touched.date && errors.date}
            sx={{ mb: 3 }}
            InputLabelProps={{ shrink: true, sx: { fontSize: '16px', fontWeight: 500 } }}
            inputProps={{ min: new Date().toLocaleDateString('en-CA'), sx: { fontSize: '14px' } }}
            disabled={loading}
          />

          <TextField
            fullWidth
            variant="standard"
            label="Descrição (Opcional)"
            placeholder="Pagamento de fornecedor"
            value={values.description}
            onChange={handleChange('description')}
            sx={{ mb: 4 }}
            InputLabelProps={{ shrink: true, sx: { fontSize: '16px', fontWeight: 500 } }}
            InputProps={{ sx: { fontSize: '14px' } }}
            disabled={loading}
          />

          <Box display="flex" gap={2}>
            <Button fullWidth variant="outlined" onClick={onCancel} disabled={loading}>
              Cancelar
            </Button>
            <Button fullWidth variant="contained" type="submit" disabled={loading || !isValid}>
              {loading ? <CircularProgress size={20} color="inherit" /> : 'Confirmar'}
            </Button>
          </Box>
        </form>
      )}
    </Formik>
  );
};

export default PixByFavorecido;
